import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import Breadcrumb from '../components/Breadcrumb';
import SectionEyebrow from '../components/SectionEyebrow';

type TierId = 'village' | 'garden' | 'estate' | 'signature';
type ScenarioId = 'conservative' | 'moderate' | 'aggressive';
type RentalMode = 'none' | 'light' | 'parttime' | 'full';

const TIERS: Record<TierId, { label: string; sub: string; entry: number; buildEst: number; nightly: number; occupancy: number }> = {
  village:   { label: 'Village',   sub: '200–300 m²',     entry: 175000,  buildEst: 250000,  nightly: 165, occupancy: 0.55 },
  garden:    { label: 'Garden',    sub: '500–1,000 m²',   entry: 350000,  buildEst: 450000,  nightly: 280, occupancy: 0.60 },
  estate:    { label: 'Estate',    sub: '2,000–4,000 m²', entry: 750000,  buildEst: 850000,  nightly: 525, occupancy: 0.62 },
  signature: { label: 'Signature', sub: '5,000+ m²',      entry: 1750000, buildEst: 1500000, nightly: 950, occupancy: 0.55 },
};

const SCENARIOS: Record<ScenarioId, { label: string; annualRate: number; comment: string }> = {
  conservative: { label: 'Conservative', annualRate: 0.149, comment: '~2× by yr 5' },
  moderate:     { label: 'Moderate',     annualRate: 0.246, comment: '~3× by yr 5' },
  aggressive:   { label: 'Aggressive',   annualRate: 0.320, comment: '~4× by yr 5' },
};

const RENTAL_MODES: Record<RentalMode, { label: string; factor: number; sub: string }> = {
  none:     { label: 'No rental',   factor: 0,   sub: 'Personal use only' },
  light:    { label: 'Light',       factor: 0.4, sub: '~80 nights/yr' },
  parttime: { label: 'Part-time',   factor: 0.7, sub: '~150 nights/yr' },
  full:     { label: 'Full rental', factor: 1.0, sub: '~210 nights/yr' },
};

const GOLD_PRICE          = 4750;
const GOLD_ANNUAL         = 0.079;
const TREASURY_RATE       = 0.04;
const RENTAL_NET_MARGIN   = 0.62;
const RENTAL_INCOME_GROWTH = 0.05;
const ANNUAL_HOA          = 4800;
const PACIFIC_MULTIPLE    = 3.0;
const CARIBBEAN_BASE_GROWTH = 0.06;
const ACQUISITION_FRICTION = 0.035;
const RENTAL_TAX_BLENDED  = 0.25;
const SALE_FRICTION       = 0.18;

function fmtK(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

function fmtFull(n: number) {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

export default function Calculator() {
  const [tierId, setTierId]       = useState<TierId>('garden');
  const [scenario, setScenario]   = useState<ScenarioId>('moderate');
  const [years, setYears]         = useState(5);
  const [rentalMode, setRentalMode] = useState<RentalMode>('parttime');
  const [buildHome, setBuildHome] = useState(false);
  const [showTaxes, setShowTaxes] = useState(false);

  const tier        = TIERS[tierId];
  const scenarioData = SCENARIOS[scenario];
  const rentalData  = RENTAL_MODES[rentalMode];

  const projection = useMemo(() => {
    const { entry, buildEst, nightly, occupancy } = tier;
    const { annualRate } = scenarioData;
    const { factor } = rentalData;
    const acquisitionCost = entry * ACQUISITION_FRICTION;

    return Array.from({ length: years + 1 }, (_, y) => {
      const lotValue = entry * Math.pow(1 + annualRate, y);

      // Home appreciates at base Caribbean market rate
      const homeValue = buildHome ? buildEst * Math.pow(1 + CARIBBEAN_BASE_GROWTH, y) : 0;

      // Pacific reference: same entry × 3× today, growing at base rate
      const pacificFuture = entry * PACIFIC_MULTIPLE * Math.pow(1 + CARIBBEAN_BASE_GROWTH, y);

      // Gold: same dollars converted to oz, grown at historical rate
      const goldValue = (entry / GOLD_PRICE) * GOLD_PRICE * Math.pow(1 + GOLD_ANNUAL, y);

      // U.S. Treasury Notes/Bonds: same dollars at 4% annual growth
      const treasuryValue = entry * Math.pow(1 + TREASURY_RATE, y);

      // Cumulative rental income (requires home, starts year 1)
      let rentalCumulative = 0;
      if (factor > 0 && buildHome && y > 0) {
        for (let yi = 1; yi <= y; yi++) {
          const nightlyThisYear = nightly * Math.pow(1 + RENTAL_INCOME_GROWTH, yi - 1);
          const nights = 365 * occupancy * factor;
          const gross = nightlyThisYear * nights;
          const net = gross * RENTAL_NET_MARGIN - ANNUAL_HOA;
          rentalCumulative += Math.max(0, net);
        }
      }

      // Pre-tax total return
      const totalInvested = entry + (buildHome ? buildEst : 0);
      const totalPortfolio = lotValue + homeValue + rentalCumulative;
      const totalReturn = totalPortfolio - totalInvested;

      // After-tax total return
      const lotGain = Math.max(0, lotValue - entry);
      const lotNetIfSold = lotValue - lotGain * SALE_FRICTION;
      const rentalAfterTax = rentalCumulative * (1 - RENTAL_TAX_BLENDED);
      const homeGain = buildHome ? Math.max(0, homeValue - buildEst) : 0;
      const homeNetIfSold = buildHome ? homeValue - homeGain * SALE_FRICTION : 0;
      const totalAfterTax =
        (lotNetIfSold - entry - acquisitionCost) +
        rentalAfterTax +
        (buildHome ? homeNetIfSold - buildEst : 0);

      return {
        year: y,
        lot: Math.round(lotValue),
        lotNet: Math.round(lotNetIfSold),
        home: Math.round(homeValue),
        portfolio: Math.round(totalPortfolio),
        pacific: Math.round(pacificFuture),
        gold: Math.round(goldValue),
        treasury: Math.round(treasuryValue),
        rental: Math.round(rentalCumulative),
        rentalNet: Math.round(rentalAfterTax),
        totalReturn: Math.round(totalReturn),
        totalAfterTax: Math.round(totalAfterTax),
        acquisitionCost: Math.round(acquisitionCost),
      };
    });
  }, [tier, scenarioData, rentalData, years, buildHome]);

  const start = projection[0];
  const final = projection[projection.length - 1];
  const totalInvested = tier.entry + (buildHome ? tier.buildEst : 0);
  const goldGain = final.gold - start.gold;
  const displayReturn = showTaxes ? final.totalAfterTax : final.totalReturn;
  const displayROIPct = (displayReturn / totalInvested) * 100;
  const outperformVsGold = final.totalReturn - goldGain;

  return (
    <div>
      {/* HERO */}
      <section className="pt-48 pb-12 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Breadcrumb current="ROI Calculator" />
          <div className="mt-10 grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <SectionEyebrow>The Founder Calculator</SectionEyebrow>
              <h1 className="mt-6 font-display text-5xl lg:text-7xl leading-[1] text-forest-900">
                What does your lot{' '}
                <span className="italic text-clay-500">actually</span> earn?
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-forest-900/70 leading-relaxed">
                Pick a tier, hold period, and rental usage. Toggle a home build to see the
                full portfolio — land appreciation, home value, and rental income combined.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section className="pb-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10">

            {/* ── INPUTS ── */}
            <div className="lg:col-span-5 space-y-10">

              {/* Lot tier */}
              <div>
                <Label>Lot tier</Label>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {(Object.keys(TIERS) as TierId[]).map((id) => (
                    <button
                      key={id}
                      onClick={() => setTierId(id)}
                      className={`p-4 text-left border transition-colors ${
                        tierId === id
                          ? 'border-forest-900 bg-forest-900 text-cream'
                          : 'border-forest-900/15 bg-cream hover:border-forest-900/40'
                      }`}
                    >
                      <div className="font-display text-lg leading-tight">{TIERS[id].label}</div>
                      <div className={`text-xs mt-1 ${tierId === id ? 'text-cream/70' : 'text-forest-900/60'}`}>
                        {TIERS[id].sub}
                      </div>
                      <div className={`text-sm mt-1.5 font-medium ${tierId === id ? 'text-sand-300' : 'text-clay-600'}`}>
                        {fmtK(TIERS[id].entry)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Hold period */}
              <div>
                <div className="flex items-baseline justify-between">
                  <Label>Hold period</Label>
                  <span className="font-display text-2xl text-forest-900">
                    {years} {years === 1 ? 'year' : 'years'}
                  </span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={years}
                  onChange={(e) => setYears(Number(e.target.value))}
                  className="mt-4 w-full accent-clay-500"
                  aria-label="Hold period in years"
                />
                <div className="mt-2 flex justify-between text-xs text-forest-900/50 font-mono">
                  <span>1 yr</span><span>5 yr</span><span>10 yr</span>
                </div>
              </div>

              {/* Appreciation scenario */}
              <div>
                <Label>Appreciation scenario</Label>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {(Object.keys(SCENARIOS) as ScenarioId[]).map((s) => (
                    <button
                      key={s}
                      onClick={() => setScenario(s)}
                      className={`px-3 py-3 text-sm border transition-colors text-center ${
                        scenario === s
                          ? 'border-clay-500 bg-clay-500 text-cream'
                          : 'border-forest-900/15 hover:border-forest-900/40'
                      }`}
                    >
                      <div className="font-medium">{SCENARIOS[s].label}</div>
                      <div className={`text-xs mt-1 ${scenario === s ? 'text-cream/70' : 'text-forest-900/50'}`}>
                        {SCENARIOS[s].comment}
                      </div>
                    </button>
                  ))}
                </div>
                <p className="mt-3 text-xs text-forest-900/60 leading-relaxed">
                  Lot appreciates at {(scenarioData.annualRate * 100).toFixed(1)}%/yr compounded.
                  Based on comparable Costa Rica Pacific masterplans 2010–2020.
                </p>
              </div>

              {/* Build a home + rental — grouped together */}
              <div className="space-y-4">
                <Label>Build a home &amp; rental income</Label>

                {/* Build toggle */}
                <button
                  onClick={() => {
                    const next = !buildHome;
                    setBuildHome(next);
                    if (next && rentalMode === 'none') setRentalMode('parttime');
                  }}
                  className="w-full flex items-center justify-between p-4 border border-forest-900/15 hover:border-forest-900/40 transition-colors"
                  aria-pressed={buildHome}
                >
                  <div>
                    <div className="text-forest-900 text-sm font-medium">
                      {buildHome ? 'Home included in portfolio' : 'Land only (no build)'}
                    </div>
                    <div className="text-xs text-forest-900/50 mt-0.5">
                      {buildHome
                        ? `Est. build cost ${fmtK(tier.buildEst)} · appreciates at ${(CARIBBEAN_BASE_GROWTH * 100).toFixed(0)}%/yr`
                        : 'Toggle on to model home value + rental income'}
                    </div>
                  </div>
                  <span className={`ml-4 flex-shrink-0 w-10 h-6 rounded-full relative transition-colors ${buildHome ? 'bg-clay-500' : 'bg-forest-900/20'}`}>
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-cream transition-transform ${buildHome ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </span>
                </button>

                {/* Rental modes — only shown when home is built */}
                {buildHome ? (
                  <div>
                    <div className="text-xs uppercase tracking-widest-2 text-forest-900/50 mb-2">Rental usage</div>
                    <div className="grid grid-cols-2 gap-2">
                      {(Object.keys(RENTAL_MODES) as RentalMode[]).map((r) => (
                        <button
                          key={r}
                          onClick={() => setRentalMode(r)}
                          className={`px-3 py-3 text-sm border transition-colors text-left ${
                            rentalMode === r
                              ? 'border-forest-700 bg-forest-700 text-cream'
                              : 'border-forest-900/15 hover:border-forest-900/40'
                          }`}
                        >
                          <div className="font-medium">{RENTAL_MODES[r].label}</div>
                          <div className={`text-xs mt-0.5 ${rentalMode === r ? 'text-cream/70' : 'text-forest-900/50'}`}>
                            {RENTAL_MODES[r].sub}
                          </div>
                        </button>
                      ))}
                    </div>
                    {rentalMode !== 'none' && (
                      <p className="mt-2 text-xs text-forest-900/60 leading-relaxed">
                        ${tier.nightly}/night · {Math.round(tier.occupancy * 100)}% base occupancy · 62% net margin after management &amp; ops.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-forest-900/40 italic pl-1">
                    Enable "Build a home" above to unlock rental income modelling.
                  </p>
                )}
              </div>

              {/* After-tax toggle */}
              <div>
                <Label>Tax view</Label>
                <button
                  onClick={() => setShowTaxes((v) => !v)}
                  className="mt-3 w-full flex items-center justify-between p-4 border border-forest-900/15 hover:border-forest-900/40 transition-colors"
                >
                  <div>
                    <span className="text-forest-900 text-sm font-medium">
                      {showTaxes ? 'After-tax estimate' : 'Pre-tax (gross)'}
                    </span>
                    <p className="text-xs text-forest-900/50 mt-0.5">
                      {showTaxes
                        ? 'CR transfer, rental & cap gains taxes applied'
                        : 'Toggle to see after-tax estimate'}
                    </p>
                  </div>
                  <span className={`w-10 h-6 rounded-full relative transition-colors ${showTaxes ? 'bg-forest-700' : 'bg-forest-900/20'}`}>
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-cream transition-transform ${showTaxes ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </span>
                </button>
              </div>
            </div>

            {/* ── RESULTS ── */}
            <div className="lg:col-span-7 space-y-6">

              {/* Main result card */}
              <div className="bg-forest-900 text-cream p-8 lg:p-12 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 -mt-10 -mr-10 rounded-full bg-clay-500/20 blur-3xl" />
                <div className="absolute bottom-0 left-0 w-60 h-60 -mb-20 -ml-20 rounded-full bg-sand-300/10 blur-3xl" />

                <div className="relative">
                  <div className="eyebrow text-sand-300">
                    {tier.label} · {scenarioData.label} · {rentalData.label}
                    {buildHome ? ' · With home' : ''}
                  </div>

                  <div className="mt-6">
                    <div className="text-xs uppercase tracking-widest-2 text-cream/60">
                      {buildHome ? 'Total portfolio at year' : 'Lot value at year'} {years}
                    </div>
                    <div className="font-display text-6xl lg:text-7xl text-cream leading-none">
                      {fmtK(showTaxes
                        ? (final.lotNet + final.home + final.rentalNet)
                        : (buildHome ? final.portfolio : final.lot)
                      )}
                    </div>
                    <div className="mt-2 text-cream/60 text-sm">
                      Total invested: {fmtK(totalInvested)}
                    </div>
                  </div>

                  <div className="my-8 h-px bg-cream/10" />

                  <div className="space-y-4">
                    <ResultRow
                      label="Lot appreciation"
                      value={`+${fmtFull(final.lot - start.lot)}`}
                    />
                    {buildHome && (
                      <ResultRow
                        label={`Home value (yr ${years})`}
                        value={fmtFull(final.home)}
                      />
                    )}
                    {rentalMode !== 'none' && buildHome && (
                      <ResultRow
                        label="Cumulative rental (net)"
                        value={fmtFull(showTaxes ? final.rentalNet : final.rental)}
                      />
                    )}
                    {buildHome && (
                      <ResultRow label="Build cost" value={`-${fmtFull(tier.buildEst)}`} />
                    )}
                    <ResultRow label="Initial lot cost" value={`-${fmtFull(tier.entry)}`} />
                  </div>

                  <div className="mt-6 pt-6 border-t border-cream/10">
                    <div className="flex items-baseline justify-between">
                      <span className="eyebrow text-sand-300">
                        {showTaxes ? 'Total return (after tax)' : 'Total return'}
                      </span>
                      <span className={`font-display text-5xl lg:text-6xl ${displayReturn >= 0 ? 'text-sand-300' : 'text-clay-300'}`}>
                        {displayReturn >= 0 ? '+' : ''}{fmtK(displayReturn)}
                      </span>
                    </div>
                    <div className="mt-2 text-cream/50 text-sm">
                      {displayReturn >= 0 ? '+' : ''}{displayROIPct.toFixed(0)}% on invested capital
                    </div>
                  </div>

                  <p className="mt-8 text-xs uppercase tracking-widest-2 text-cream/50 leading-relaxed">
                    Illustrative only · Not investment advice · Consult your CPA
                  </p>
                </div>
              </div>

              {/* Gold comparison */}
              <div className="bg-cream border border-forest-900/15 p-8">
                <div className="eyebrow text-clay-600">The gold-bar problem</div>
                <h3 className="mt-3 font-display text-xl lg:text-2xl text-forest-900 leading-snug">
                  For {fmtK(tier.entry)} you could buy{' '}
                  <span className="text-clay-600">
                    {(tier.entry / GOLD_PRICE).toFixed(1)} oz of gold.
                  </span>{' '}
                  In {years} {years === 1 ? 'year' : 'years'} those bars would be worth{' '}
                  <span className="font-medium">{fmtK(final.gold)}</span>.
                </h3>
                <p className="mt-3 text-sm text-forest-900/70 leading-relaxed">
                  But you can't sleep in a gold bar, rent it on Airbnb, or watch the river run past it.
                  Land in a place people want to be <em>appreciates and pays you to own it.</em>
                </p>
                <div className="mt-6 space-y-2">
                  <Bar
                    label="Nativa total return"
                    value={displayReturn}
                    max={Math.max(displayReturn, goldGain, 1)}
                    color="bg-forest-700"
                  />
                  <Bar
                    label="Gold return (same $)"
                    value={goldGain}
                    max={Math.max(displayReturn, goldGain, 1)}
                    color="bg-sand-400"
                  />
                </div>
                {outperformVsGold > 0 && (
                  <p className="mt-4 text-xs text-forest-900/60">
                    Nativa outperforms gold by{' '}
                    <span className="font-medium text-forest-900">{fmtK(outperformVsGold)}</span> over {years} {years === 1 ? 'year' : 'years'}.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* ── CHART ── */}
          <div className="mt-16 border border-forest-900/15 bg-white p-8 lg:p-12">
            <SectionEyebrow>Value over time</SectionEyebrow>
            <h2 className="mt-4 font-display text-2xl lg:text-3xl text-forest-900 leading-tight">
              Your lot vs. gold vs. U.S. Treasuries.
            </h2>
            <p className="mt-3 text-sm text-forest-900/60 leading-relaxed max-w-2xl">
              U.S. Treasury Notes/Bonds modeled at 4% annual growth. Gold grown at its historical
              50-year rate. Same starting dollars in each case.
            </p>

            <div className="mt-8" style={{ width: '100%', height: 380 }}>
              <ResponsiveContainer>
                <LineChart data={projection} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#e8e0cc" vertical={false} />
                  <XAxis
                    dataKey="year"
                    tickFormatter={(y) => (y === 0 ? 'Today' : `Yr ${y}`)}
                    tick={{ fontSize: 11, fill: '#4a7050' }}
                    stroke="#4a7050"
                  />
                  <YAxis
                    tickFormatter={fmtK}
                    tick={{ fontSize: 11, fill: '#4a7050' }}
                    stroke="#4a7050"
                    width={68}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#1f2f24',
                      border: 'none',
                      borderRadius: 2,
                      color: '#faf7f0',
                      fontSize: 12,
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                    labelFormatter={(y) => (y === 0 ? 'Today' : `Year ${y}`)}
                    formatter={(v, name) => [fmtFull(Number(v)), String(name)]}
                    itemStyle={{ color: '#faf7f0' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 16 }} iconType="circle" />
                  <Line
                    name={buildHome ? 'Nativa portfolio (lot + home + rental)' : 'Nativa lot value'}
                    type="monotone"
                    dataKey={buildHome ? 'portfolio' : 'lot'}
                    stroke="#2e4733"
                    strokeWidth={3}
                    dot={{ r: 3, fill: '#2e4733' }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    name="Gold (same $ invested)"
                    type="monotone"
                    dataKey="gold"
                    stroke="#a89660"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    dot={{ r: 3, fill: '#a89660' }}
                  />
                  <Line
                    name="U.S. Treasuries @ 4%/yr"
                    type="monotone"
                    dataKey="treasury"
                    stroke="#6b8cae"
                    strokeWidth={2}
                    strokeDasharray="4 3"
                    dot={{ r: 3, fill: '#6b8cae' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </section>

      {/* ASSUMPTIONS */}
      <section className="py-20 bg-forest-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <SectionEyebrow>What is under the hood</SectionEyebrow>
          <h2 className="mt-6 font-display text-3xl lg:text-4xl text-forest-900 leading-tight">
            The assumptions, in{' '}
            <span className="italic text-clay-500">plain English.</span>
          </h2>
          <div className="mt-10 grid md:grid-cols-2 gap-8 text-forest-900/80 leading-relaxed">
            <div>
              <h3 className="font-display text-xl text-forest-900 mb-2">Lot appreciation</h3>
              <p className="text-sm">
                Phase 1 entry pricing locked at founder rates. Conservative 14.9%/yr ≈ 2× by year 5,
                Moderate 24.6% ≈ 3×, Aggressive 32% ≈ 4×. Based on comparable Costa Rica Pacific
                masterplans from 2010–2020.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl text-forest-900 mb-2">Rental income</h3>
              <p className="text-sm">
                Requires a built home. Nightly rates scale by tier ($165–$950). 62% net margin
                after property management, insurance, and operations. HOA $4,800/yr. Nightly rate
                grows 5%/yr as the destination matures.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl text-forest-900 mb-2">Gold comparison</h3>
              <p className="text-sm">
                Spot gold ~$4,750/oz (April 2026). Historical 50-year compound annual return: 7.9%.
                Same starting dollars converted to ounces at today's spot price and grown at the
                historical rate. Reference benchmark only.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl text-forest-900 mb-2">Home value</h3>
              <p className="text-sm">
                Build cost is added to your total investment. The home itself appreciates at the
                base Caribbean market rate (6%/yr) and its future value is included in your total
                portfolio. Actual construction cost varies with design and finish level.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl text-forest-900 mb-2">U.S. Treasuries</h3>
              <p className="text-sm">
                Same starting dollars invested in U.S. Treasury Notes/Bonds at a fixed 4% annual
                growth rate — a common benchmark for low-risk, government-backed returns. Modeled
                as a simple compound return with no fees or taxes. Reference benchmark only.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl text-forest-900 mb-2">Pacific catch-up</h3>
              <p className="text-sm">
                Comparable land on Costa Rica's Pacific coast currently costs ~3× more. The Pacific
                reference line shows a similar lot growing at standard 6% CR market growth — the
                visible gap is what Caribbean Phase 1 buyers are positioned to close.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl text-forest-900 mb-2">Tax &amp; friction</h3>
              <p className="text-sm">
                Acquisition 3.5% (CR transfer tax, legal, notary). Rental tax 25% blended (CR
                ~13–15% + US tax net of foreign tax credit, 24% bracket). Sale friction 18% of gain
                (CR cap gains 15% + commission). Estimate only — consult your CPA.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-cream">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <SectionEyebrow className="justify-center">Like the numbers?</SectionEyebrow>
          <h2 className="mt-6 font-display text-4xl lg:text-6xl leading-tight text-forest-900">
            Hold your spot.{' '}
            <span className="italic text-clay-500">Then come walk the river.</span>
          </h2>
          <p className="mt-6 text-forest-900/70 leading-relaxed">
            Refundable $5,000 deposit reserves a tier slot in the Phase 1 queue.
            No specific lot committed yet — you choose that when you visit.
          </p>
          <Link
            to="/reserve"
            className="mt-10 inline-flex items-center gap-2 px-7 py-4 bg-forest-700 text-cream font-medium tracking-wide hover:bg-forest-900 transition-colors"
          >
            Reserve a Lot →
          </Link>
        </div>
      </section>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <label className="eyebrow text-forest-900/70 block">{children}</label>;
}

function ResultRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-cream/70 text-sm uppercase tracking-wider">{label}</span>
      <span className="font-display text-2xl text-cream tabular-nums">{value}</span>
    </div>
  );
}

function Bar({
  label,
  value,
  max,
  color,
}: {
  label: string;
  value: number;
  max: number;
  color: string;
}) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div>
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-forest-900/70">{label}</span>
        <span className="font-display text-lg text-forest-900 tabular-nums">
          {value >= 0 ? '+' : ''}{fmtK(value)}
        </span>
      </div>
      <div className="mt-1 h-2 bg-forest-900/10 overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
